import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, CalendarDays, Compass, Ticket, Hourglass, Users, Star,
         MessageCircle, Facebook, Twitter, Link2,
         Sun, CloudSun, CloudFog, CloudRain, Snowflake, CloudLightning } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFF_STYLE = {
  soft: "text-[#7FB383] border-[#4E8752]/50 bg-[#4E8752]/10",
  trail: "text-ochre border-ochre/50 bg-ochre/10",
  extreme: "text-[#E86A50] border-[#D94235]/50 bg-[#D94235]/10",
};

const FALLBACK_GALLERY = [
  "https://images.unsplash.com/photo-1485465109746-b76ae8d7eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1605019659411-f6dd1d229f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxvdmVybGFuZCUyMGNhbXBpbmclMjA0eDQlMjBleHBlZGl0aW9ufGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1636138105085-3e9381a8e438?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxvdmVybGFuZCUyMGNhbXBpbmclMjA0eDQlMjBleHBlZGl0aW9ufGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1672334440489-7945a743b823?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
];

function parseCoords(coords) {
  const m = (coords || "").match(/([\d.]+)°\s*([NS])\D+([\d.]+)°\s*([EW])/);
  if (!m) return null;
  const lat = (m[2] === "S" ? -1 : 1) * parseFloat(m[1]);
  const lon = (m[4] === "W" ? -1 : 1) * parseFloat(m[3]);
  return { lat, lon };
}

export default function EventPage() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const { user, setAuthOpen } = useAuth();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [booked, setBooked] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [weather, setWeather] = useState(null); // null = loading
  const [reviewsData, setReviewsData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/events/${id}`)
      .then((r) => setEv(r.data))
      .catch(() => setNotFound(true));
    axios
      .get(`${API}/events/${id}/weather`)
      .then((r) => setWeather(r.data))
      .catch(() => setWeather({ available: false }));
    axios
      .get(`${API}/events/${id}/reviews`)
      .then((r) => setReviewsData(r.data))
      .catch(() => setReviewsData({ reviews: [], avg: null, count: 0 }));
  }, [id]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/members/me/bookings`, { withCredentials: true })
      .then((r) => setBooked(r.data.some((e) => e.id === id)))
      .catch(() => {});
    axios
      .get(`${API}/members/me/waitlist`, { withCredentials: true })
      .then((r) => setWaitlisted(r.data.includes(id)))
      .catch(() => {});
  }, [user, id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="event-not-found">
        <div className="text-center">
          <p className="font-display text-3xl font-extrabold uppercase text-bone mb-6">{t.detail.notFound}</p>
          <Link to="/" className="font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-7 py-3.5 hover:bg-sand transition-colors">
            {t.detail.back}
          </Link>
        </div>
      </div>
    );
  }

  if (!ev) {
    return <div className="min-h-screen bg-ink" data-testid="event-loading" />;
  }

  const left = ev.spots_total - ev.spots_taken;
  const soldOut = left <= 0;
  const gallery = ev.gallery?.length ? [ev.image, ...ev.gallery].filter(Boolean) : [ev.image, ...FALLBACK_GALLERY].filter(Boolean);
  const coords = parseCoords(ev.coords);
  const fmt = (iso) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  const book = async () => {
    if (!user) {
      toast(t.events.loginToBook);
      setAuthOpen(true);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/events/${ev.id}/book?lang=${lang}`, {}, { withCredentials: true });
      setEv((e) => ({ ...e, spots_taken: data.spots_taken }));
      setBooked(true);
      toast.success(t.events.bookSuccess);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.events.loadError);
    }
  };

  const joinWaitlist = async () => {
    if (!user) {
      toast(t.events.loginToBook);
      setAuthOpen(true);
      return;
    }
    try {
      await axios.post(`${API}/events/${ev.id}/waitlist?lang=${lang}`, {}, { withCredentials: true });
      setWaitlisted(true);
      toast.success(t.events.waitlistSuccess);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.events.loadError);
    }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="event-detail-page">
      <header className="border-b border-line bg-surface/60">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src="/logo.png" alt="Dust Republic" className="h-10 w-auto" />
          <Link
            to="/"
            data-testid="event-back-link"
            className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors"
          >
            <ArrowLeft size={14} />
            {t.detail.back}
          </Link>
        </div>
      </header>

      <div className="relative h-[46vh] min-h-[320px] overflow-hidden">
        {ev.image && (
          <motion.img
            initial={{ scale: 1.06, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            src={ev.image}
            alt={ev.title[lang]}
            className="w-full h-full object-cover warm-grade"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-[1300px] mx-auto px-5 sm:px-8 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex flex-wrap items-center gap-2.5 mb-4">
                <span className={`font-telemetry text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 backdrop-blur-sm ${DIFF_STYLE[ev.difficulty]}`}>
                  {t.events.difficulties[ev.difficulty]}
                </span>
                <span className="font-telemetry text-[10px] uppercase tracking-[0.15em] bg-ink/70 border border-sand/15 text-sand px-3 py-1.5 backdrop-blur-sm">
                  {fmt(ev.date)} — {ev.days} {ev.days > 1 ? t.events.days : t.events.day}
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tight leading-[0.92] text-bone">
                {ev.title[lang]}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-5 font-telemetry text-[11px] uppercase tracking-[0.18em] text-sand">
                <span className="flex items-center gap-2">
                  <MapPin size={13} className="text-blaze" />
                  {ev.location}
                </span>
                <span className="flex items-center gap-2">
                  <Compass size={13} className="text-blaze" />
                  {ev.coords}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-12 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="lg:col-span-7"
        >
          <p className="text-sm sm:text-base leading-relaxed text-ash mb-10">{ev.description[lang]}</p>

          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-bone mb-6" data-testid="event-program-heading">
            {t.detail.program}
          </h2>
          <ol className="flex flex-col" data-testid="event-program-list">
            {ev.itinerary[lang].map((step, i) => (
              <li key={i} className="group flex items-start gap-5 border-t border-line py-5 last:border-b">
                <span className="font-telemetry text-stroke-blaze text-3xl font-bold leading-none shrink-0 w-12">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-sm text-sand/85 leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>

          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-bone mt-12 mb-6">
            {t.detail.gallery}
          </h2>
          <div className="grid grid-cols-2 gap-3" data-testid="event-gallery">
            {gallery.slice(0, 5).map((img, i) => (
              <figure key={i} className={`relative overflow-hidden border border-line group ${i === 0 ? "col-span-2 h-56 sm:h-72" : "h-40 sm:h-48"}`}>
                <img
                  src={img}
                  alt={`${ev.title[lang]} — ${i + 1}`}
                  loading="lazy"
                  data-testid={`event-gallery-img-${i}`}
                  className="w-full h-full object-cover warm-grade group-hover:scale-105 transition-transform duration-700"
                />
              </figure>
            ))}
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-bone mt-12 mb-6">
            {t.detail.reviews}
          </h2>
          <div data-testid="event-reviews">
            {reviewsData && reviewsData.count > 0 ? (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-display text-4xl font-black text-blaze leading-none">{reviewsData.avg}</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={16} className={n <= Math.round(reviewsData.avg) ? "text-ochre fill-ochre" : "text-line"} />
                    ))}
                  </div>
                  <span className="font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut">
                    ({reviewsData.count})
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  {reviewsData.reviews.map((r, i) => (
                    <div key={i} data-testid={`review-card-${i}`} className="border border-line bg-surface/60 p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-bone">{r.user_name}</span>
                        <span className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} size={12} className={n <= r.rating ? "text-ochre fill-ochre" : "text-line"} />
                          ))}
                        </span>
                      </div>
                      {r.comment && <p className="text-xs text-ash leading-relaxed">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut">
                {t.detail.noReviews}
              </p>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="lg:col-span-5 flex flex-col gap-6"
        >
          <div className="border border-line bg-surface p-7" data-testid="event-availability-card">
            <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-4">
              {t.detail.availability}
            </p>
            <div className="flex items-end justify-between mb-3">
              <span className="font-display text-4xl font-black text-bone leading-none">
                {soldOut ? 0 : left}
                <span className="text-lg text-sandmut">/{ev.spots_total}</span>
              </span>
              <span className="font-telemetry text-[11px] uppercase tracking-[0.15em] text-sand">
                {t.events.from} €{ev.price}
              </span>
            </div>
            <div className="h-[3px] bg-line w-full mb-6">
              <div
                className={`h-full ${soldOut ? "bg-[#D94235]" : "bg-blaze"}`}
                style={{ width: `${Math.min(100, (ev.spots_taken / ev.spots_total) * 100)}%` }}
              />
            </div>
            <button
              data-testid="event-detail-book-btn"
              onClick={soldOut && !booked ? joinWaitlist : book}
              disabled={booked || waitlisted}
              className={`w-full font-telemetry text-[11px] uppercase tracking-[0.22em] px-5 py-4 flex items-center justify-center gap-2.5 transition-colors duration-300 ${
                booked
                  ? "bg-[#4E8752]/20 text-[#7FB383] border border-[#4E8752]/40 cursor-default"
                  : waitlisted
                  ? "bg-ochre/15 text-ochre border border-ochre/40 cursor-default"
                  : soldOut
                  ? "border border-blaze/60 text-blaze hover:bg-blaze hover:text-ink"
                  : "bg-blaze text-ink hover:bg-sand"
              }`}
            >
              {soldOut && !booked && !waitlisted ? <Hourglass size={14} /> : <Ticket size={14} />}
              {booked ? t.events.booked : waitlisted ? t.events.inWaitlist : soldOut ? t.events.joinWaitlist : t.events.book}
            </button>
            <div className="mt-5 flex items-center gap-2.5 font-telemetry text-[10px] uppercase tracking-[0.18em] text-sandmut">
              <Users size={13} className="text-ochre" />
              {ev.spots_taken} / {ev.spots_total}
            </div>
            {ev.requirements && Object.keys(ev.requirements).length > 0 &&
              (ev.requirements.min_tires !== "street" || ev.requirements.min_lift !== "stock" || ev.requirements.winch) && (
              <div className="mt-4 border-t border-line pt-4" data-testid="event-requirements">
                <p className="font-telemetry text-[9px] uppercase tracking-[0.2em] text-sandmut mb-2.5">
                  {t.detail.requirements}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {ev.requirements.min_tires && ev.requirements.min_tires !== "street" && (
                    <span className="font-telemetry text-[9px] uppercase tracking-[0.15em] border border-ochre/40 text-ochre px-2 py-1">
                      {t.member.tires}: {t.member.tiresOpts[ev.requirements.min_tires]}
                    </span>
                  )}
                  {ev.requirements.min_lift && ev.requirements.min_lift !== "stock" && (
                    <span className="font-telemetry text-[9px] uppercase tracking-[0.15em] border border-ochre/40 text-ochre px-2 py-1">
                      {t.member.lift}: {t.member.liftOpts[ev.requirements.min_lift]}
                    </span>
                  )}
                  {ev.requirements.winch && (
                    <span className="font-telemetry text-[9px] uppercase tracking-[0.15em] border border-blaze/50 text-blaze px-2 py-1">
                      {t.member.winch}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {coords && (
            <div className="border border-line bg-surface overflow-hidden" data-testid="event-map">
              <p className="px-6 pt-5 font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze">
                {t.detail.map} — {ev.coords}
              </p>
              <iframe
                title={`Mappa ${ev.location}`}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${coords.lon - 0.35}%2C${coords.lat - 0.22}%2C${coords.lon + 0.35}%2C${coords.lat + 0.22}&layer=mapnik&marker=${coords.lat}%2C${coords.lon}`}
                className="w-full h-64 mt-4 border-0 grayscale-[35%] contrast-[1.05]"
                loading="lazy"
              />
            </div>
          )}

          <div className="border border-line bg-surface p-7" data-testid="event-weather-card">
            <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-4">
              {t.detail.weather} — {fmt(ev.date)}
            </p>
            {weather === null ? (
              <p className="font-telemetry text-xs text-sandmut">…</p>
            ) : !weather.available ? (
              <p className="text-xs text-ash leading-relaxed">{t.detail.weatherNote}</p>
            ) : (
              (() => {
                const code = weather.code ?? 0;
                const Icon = code <= 1 ? Sun : code <= 3 ? CloudSun : code <= 48 ? CloudFog : code <= 67 ? CloudRain : code <= 77 ? Snowflake : code <= 82 ? CloudRain : CloudLightning;
                const key = code <= 1 ? "wxClear" : code <= 3 ? "wxCloudy" : code <= 48 ? "wxFog" : code <= 67 ? "wxRain" : code <= 77 ? "wxSnow" : code <= 82 ? "wxRain" : "wxStorm";
                return (
                  <div className="flex items-center gap-5" data-testid="event-weather-forecast">
                    <Icon size={38} strokeWidth={1.4} className="text-ochre shrink-0" />
                    <div>
                      <p className="font-display text-3xl font-black text-bone leading-none">
                        {Math.round(weather.tmax)}°<span className="text-lg text-sandmut"> / {Math.round(weather.tmin)}°</span>
                      </p>
                      <p className="mt-1.5 font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut">
                        {t.detail[key]} — {t.detail.precip} {weather.precip ?? 0}%
                      </p>
                    </div>
                  </div>
                );
              })()
            )}
          </div>

          <div className="border border-line bg-surface p-7" data-testid="event-share-card">
            <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-4">
              {t.detail.share}
            </p>
            <div className="flex gap-2.5">
              {(() => {
                const url = window.location.href;
                const text = `${ev.title[lang]} — DUST REPUBLIC`;
                return (
                  <>
                    <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`} target="_blank" rel="noopener noreferrer"
                      className="border border-line p-3 text-sandmut hover:text-blaze hover:border-blaze transition-colors">
                      <MessageCircle size={16} />
                    </a>
                    <a data-testid="share-facebook-btn" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer"
                      className="border border-line p-3 text-sandmut hover:text-blaze hover:border-blaze transition-colors">
                      <Facebook size={16} />
                    </a>
                    <a data-testid="share-x-btn" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer"
                      className="border border-line p-3 text-sandmut hover:text-blaze hover:border-blaze transition-colors">
                      <Twitter size={16} />
                    </a>
                    <button
                      data-testid="share-copy-link-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(url).then(() => toast.success(t.detail.copied));
                      }}
                      className="border border-line p-3 text-sandmut hover:text-blaze hover:border-blaze transition-colors"
                    >
                      <Link2 size={16} />
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
